import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthActions, useAuthState } from './AuthContext';
import { authAPI } from '@/lib/api/auth';
import { getWebSocketCloseAction } from '@/lib/auth-runtime';
import logger from '@/lib/utils/logger';
import {
  GlobalWebSocketContext,
  getStableEventIdentity,
  type GlobalWebSocketContextType,
  type GlobalWebSocketEvent,
  type GlobalWebSocketOutboundMessage,
  type WebSocketHandler,
} from './GlobalWebSocketState';

interface GlobalWebSocketProviderProps {
  children: ReactNode;
  webSocketBaseUrl?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseIncomingEvent(rawData: unknown): GlobalWebSocketEvent {
  if (typeof rawData !== 'string') {
    throw new Error('WebSocket payload must be text');
  }

  const parsed: unknown = JSON.parse(rawData);
  if (!isRecord(parsed) || typeof parsed.type !== 'string') {
    throw new Error('WebSocket payload must contain a string event type');
  }
  const { type, session_id: sessionId, ...payload } = parsed;
  if (sessionId !== undefined && typeof sessionId !== 'string') {
    throw new Error('WebSocket session_id must be a string');
  }

  return {
    ...payload,
    type,
    ...(sessionId === undefined ? {} : { session_id: sessionId }),
  };
}

function getWebSocketBaseUrl(): string {
  const rawConfiguredUrl: unknown = import.meta.env.VITE_WS_BASE_URL;
  if (
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
    typeof rawConfiguredUrl === 'string' &&
    rawConfiguredUrl.trim() !== ''
  ) {
    return rawConfiguredUrl.replace(/\/+$/, '');
  }

  return window.location.origin.replace(/^http/, 'ws');
}

const GLOBAL_EVENT_TYPES = new Set([
  'ai_bulk_status',
  'ai_job_status',
  'ai_response',
  'balance_update',
  'blur_status_changed',
  'character_status_update',
  'character_typing',
  'chat_message',
  'chat_status',
  'creator_post_engagement',
  'gift_image_job_status',
  'location_distribution_status',
  'message',
  'message_saved',
  'message_rejected',
  'new_match',
  'notification',
  'notification_count_update',
  'profile_visit',
  'reward_granted',
  'subscription_updated',
  'swipe_balance_update',
  'transaction_completed',
  'typing',
  'unread_count_update',
]);

const PING_INTERVAL_MS = 20_000;
const PONG_TIMEOUT_MS = 25_000;
const DEDUPLICATION_TTL_MS = 30_000;
const MAX_PENDING_MESSAGES = 50;
const MAX_PENDING_BYTES = 128_000;
const HANDSHAKE_TIMEOUT_MS = 10_000;

function pendingStorageKey(userId: number | string): string {
  return `erochat:pending-chat:${userId}`;
}

function readPersistedCommands(userId: number | string): GlobalWebSocketOutboundMessage[] {
  try {
    const raw = sessionStorage.getItem(pendingStorageKey(userId));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is GlobalWebSocketOutboundMessage =>
          isRecord(item) &&
          item.type === 'chat_message' &&
          typeof item.client_message_id === 'string' &&
          typeof item.session_id === 'string'
      )
      .slice(-MAX_PENDING_MESSAGES);
  } catch (storageError: unknown) {
    logger.error('Could not restore pending chat commands', storageError);
    return [];
  }
}

export function GlobalWebSocketProvider({ children, webSocketBaseUrl }: GlobalWebSocketProviderProps): ReactElement {
  const { token, user } = useAuthState();
  const userId = user?.id ?? null;
  const { logout, refreshTokenOnActivity } = useAuthActions();
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Set<WebSocketHandler>>(new Set());
  const sessionHandlersRef = useRef<Map<string, Set<WebSocketHandler>>>(new Map());
  const joinedSessionsRef = useRef<Set<string>>(new Set());
  const acknowledgedSessionsRef = useRef<Set<string>>(new Set());
  const pendingMessagesRef = useRef<GlobalWebSocketOutboundMessage[]>([]);
  const pendingCommandsRef = useRef<Map<string, GlobalWebSocketOutboundMessage>>(new Map());
  const seenEventsRef = useRef<Map<string, number>>(new Map());
  const reconnectTimeoutRef = useRef<number | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const lastPongAtRef = useRef(Date.now());
  const connectedRef = useRef(false);
  const authRecoveryAttemptedRef = useRef(false);
  const storageUserIdRef = useRef<number | string | null>(null);

  const persistPendingCommands = useCallback((): void => {
    if (userId === null) return;
    const commands = [...pendingCommandsRef.current.values()];
    const serialized = JSON.stringify(commands);
    if (serialized.length > MAX_PENDING_BYTES) {
      throw new Error('Pending chat command storage limit exceeded');
    }
    if (commands.length === 0) {
      sessionStorage.removeItem(pendingStorageKey(userId));
    } else {
      sessionStorage.setItem(pendingStorageKey(userId), serialized);
    }
  }, [userId]);

  const flushPendingMessages = useCallback((socket: WebSocket): void => {
    if (!connectedRef.current || socket.readyState !== WebSocket.OPEN) return;
    while (pendingMessagesRef.current.length > 0) {
      const nextMessage = pendingMessagesRef.current.shift();
      if (nextMessage !== undefined) socket.send(JSON.stringify(nextMessage));
    }
    for (const command of pendingCommandsRef.current.values()) {
      const sessionId = command.session_id;
      if (typeof sessionId === 'string' && acknowledgedSessionsRef.current.has(sessionId)) {
        socket.send(JSON.stringify(command));
      }
    }
  }, []);

  const dispatchEvent = useCallback((event: GlobalWebSocketEvent): void => {
    if (event.session_id !== undefined) {
      sessionHandlersRef.current.get(event.session_id)?.forEach((handler) => {
        try {
          handler(event);
        } catch (handlerError: unknown) {
          logger.error('Session WebSocket handler failed', handlerError, event);
        }
      });
    }

    if (event.session_id === undefined || GLOBAL_EVENT_TYPES.has(event.type)) {
      handlersRef.current.forEach((handler) => {
        try {
          handler(event);
        } catch (handlerError: unknown) {
          logger.error('Global WebSocket handler failed', handlerError, event);
        }
      });
    }
  }, []);

  const isDuplicateEvent = useCallback((event: GlobalWebSocketEvent): boolean => {
    const identity = getStableEventIdentity(event);
    if (identity === null) return false;

    const now = Date.now();
    const previousTimestamp = seenEventsRef.current.get(identity);
    seenEventsRef.current.set(identity, now);

    for (const [key, timestamp] of seenEventsRef.current) {
      if (now - timestamp > DEDUPLICATION_TTL_MS) seenEventsRef.current.delete(key);
    }

    return previousTimestamp !== undefined && now - previousTimestamp <= DEDUPLICATION_TTL_MS;
  }, []);

  const connect = useCallback((): void => {
    if (
      token === null ||
      !navigator.onLine ||
      wsRef.current?.readyState === WebSocket.OPEN ||
      isConnectingRef.current
    ) {
      return;
    }

    isConnectingRef.current = true;
    shouldReconnectRef.current = true;
    const authQuery = token !== 'cookie' ? `?token=${encodeURIComponent(token)}` : '';
    const ws = new WebSocket(`${webSocketBaseUrl ?? getWebSocketBaseUrl()}/api/v1/websocket/ws${authQuery}`);
    wsRef.current = ws;
    let pingInterval: ReturnType<typeof setInterval> | undefined;
    let handshakeTimeout: ReturnType<typeof setTimeout> | undefined;

    ws.onopen = (): void => {
      if (wsRef.current !== ws) {
        ws.close(1000, 'Stale connection');
        return;
      }
      isConnectingRef.current = false;
      lastPongAtRef.current = Date.now();
      handshakeTimeout = setTimeout(() => {
        if (!connectedRef.current && ws.readyState === WebSocket.OPEN) {
          logger.warn('WebSocket application handshake timed out');
          ws.close(4003, 'Handshake timeout');
        }
      }, HANDSHAKE_TIMEOUT_MS);

      pingInterval = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          if (pingInterval !== undefined) clearInterval(pingInterval);
          return;
        }

        const silenceDuration = Date.now() - lastPongAtRef.current;
        if (silenceDuration > PONG_TIMEOUT_MS) {
          logger.warn('WebSocket pong timeout; reconnecting', { silenceDuration });
          if (pingInterval !== undefined) clearInterval(pingInterval);
          ws.close(4000, 'Pong timeout');
          return;
        }

        ws.send(JSON.stringify({ type: 'ping' }));
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (messageEvent: MessageEvent<unknown>): void => {
      try {
        const event = parseIncomingEvent(messageEvent.data);

        if (event.type === 'connected') {
          if (handshakeTimeout !== undefined) clearTimeout(handshakeTimeout);
          connectedRef.current = true;
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
          authRecoveryAttemptedRef.current = false;
          for (const sessionId of joinedSessionsRef.current) {
            ws.send(JSON.stringify({ type: 'join_session', session_id: sessionId }));
          }
          flushPendingMessages(ws);
          dispatchEvent(event);
          return;
        }

        if (event.type === 'session_joined' && typeof event.session_id === 'string') {
          acknowledgedSessionsRef.current.add(event.session_id);
          flushPendingMessages(ws);
        }

        if (
          event.type === 'message_saved' &&
          typeof event.client_message_id === 'string' &&
          pendingCommandsRef.current.delete(event.client_message_id)
        ) {
          persistPendingCommands();
        }
        if (
          event.type === 'message_rejected' &&
          typeof event.client_message_id === 'string' &&
          pendingCommandsRef.current.delete(event.client_message_id)
        ) {
          persistPendingCommands();
        }

        if (event.type === 'pong') {
          lastPongAtRef.current = Date.now();
          return;
        }
        if (event.type === 'server_ping') {
          if (ws.readyState !== WebSocket.OPEN) {
            throw new Error('Cannot answer server ping on a closed WebSocket');
          }
          ws.send(JSON.stringify({ type: 'server_pong' }));
          return;
        }
        if (isDuplicateEvent(event)) return;

        if (event.type === 'chat_blocked') {
          // A chat was safety-blocked mid-session: refresh the session list so
          // the affected chat flips to its locked (can_chat=false) state live —
          // both in the list card and in the open conversation, which read
          // can_chat from this same cache — without a page reload.
          void queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
          dispatchEvent(event);
          return;
        }

        if (
          (event.type === 'message' ||
            event.type === 'chat_message' ||
            event.type === 'ai_response' ||
            event.type === 'typing') &&
          document.visibilityState === 'visible' &&
          document.hasFocus()
        ) {
          void refreshTokenOnActivity().catch((refreshError: unknown) => {
            logger.warn('Activity-based token refresh failed', refreshError);
          });
        }

        if (event.type === 'error') logger.error('WebSocket server error', event);
        dispatchEvent(event);
      } catch (messageError: unknown) {
        logger.error('WebSocket message processing failed', messageError, messageEvent.data);
      }
    };

    ws.onerror = (socketError: Event): void => {
      logger.warn('WebSocket transport error', { socketError, readyState: ws.readyState, url: ws.url });
    };

    ws.onclose = (closeEvent: CloseEvent): void => {
      if (wsRef.current !== ws) return;
      setIsConnected(false);
      connectedRef.current = false;
      acknowledgedSessionsRef.current.clear();
      isConnectingRef.current = false;
      wsRef.current = null;
      if (pingInterval !== undefined) clearInterval(pingInterval);
      if (handshakeTimeout !== undefined) clearTimeout(handshakeTimeout);

      const authCloseAction = getWebSocketCloseAction(closeEvent.code, authRecoveryAttemptedRef.current);
      if (authCloseAction === 'refresh') {
        authRecoveryAttemptedRef.current = true;
        logger.info('WebSocket authentication expired; refreshing session once', {
          code: closeEvent.code,
          reason: closeEvent.reason,
        });
        void authAPI
          .refresh()
          .then(() => {
            if (!shouldReconnectRef.current) return;
            reconnectTimeoutRef.current = window.setTimeout(connect, 0);
          })
          .catch((refreshError: unknown) => {
            logger.error('WebSocket authentication recovery failed', refreshError);
            logout();
          });
        return;
      }

      if (authCloseAction === 'logout') {
        logger.error('WebSocket authentication failed after refresh', {
          code: closeEvent.code,
          reason: closeEvent.reason,
        });
        logout();
        return;
      }

      if (!shouldReconnectRef.current) return;

      reconnectAttemptsRef.current += 1;
      const reconnectDelay = Math.min(1000 * 1.5 ** Math.min(reconnectAttemptsRef.current, 10), 30_000);
      const jitteredReconnectDelay = reconnectDelay * (0.75 + Math.random() * 0.5);
      logger.warn('WebSocket disconnected; reconnect scheduled', {
        attempt: reconnectAttemptsRef.current,
        code: closeEvent.code,
        delayMs: Math.round(jitteredReconnectDelay),
      });
      reconnectTimeoutRef.current = window.setTimeout(connect, jitteredReconnectDelay);
    };
  }, [
    dispatchEvent,
    queryClient,
    flushPendingMessages,
    isDuplicateEvent,
    logout,
    persistPendingCommands,
    refreshTokenOnActivity,
    token,
    webSocketBaseUrl,
  ]);

  useEffect(() => {
    const previousUserId = storageUserIdRef.current;
    if (previousUserId !== null && previousUserId !== userId) {
      sessionStorage.removeItem(pendingStorageKey(previousUserId));
    }
    storageUserIdRef.current = userId;
    if (userId === null) {
      pendingCommandsRef.current.clear();
    } else {
      pendingCommandsRef.current = new Map(
        readPersistedCommands(userId).map((command) => [command.client_message_id as string, command])
      );
    }
  }, [userId]);

  useEffect(() => {
    const acknowledgedSessions = acknowledgedSessionsRef.current;
    if (token !== null) connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current !== undefined) clearTimeout(reconnectTimeoutRef.current);
      const socket = wsRef.current;
      if (socket !== null && socket.readyState < WebSocket.CLOSING) {
        socket.close(1000, 'Provider cleanup');
      }
      wsRef.current = null;
      isConnectingRef.current = false;
      connectedRef.current = false;
      acknowledgedSessions.clear();
      setIsConnected(false);
    };
  }, [connect, token]);

  useEffect(() => {
    const reconnectNow = (): void => {
      if (token === null || !navigator.onLine) return;
      if (reconnectTimeoutRef.current !== undefined) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      if (wsRef.current === null || wsRef.current.readyState >= WebSocket.CLOSING) {
        connect();
      }
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') reconnectNow();
    };
    window.addEventListener('online', reconnectNow);
    window.addEventListener('pageshow', reconnectNow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', reconnectNow);
      window.removeEventListener('pageshow', reconnectNow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [connect, token]);

  const subscribe = useCallback((handler: WebSocketHandler): (() => void) => {
    handlersRef.current.add(handler);
    return () => handlersRef.current.delete(handler);
  }, []);

  const subscribeToSession = useCallback((sessionId: string, handler: WebSocketHandler): (() => void) => {
    const existingHandlers = sessionHandlersRef.current.get(sessionId);
    if (existingHandlers === undefined) {
      sessionHandlersRef.current.set(sessionId, new Set([handler]));
    } else {
      existingHandlers.add(handler);
    }

    return () => {
      const handlers = sessionHandlersRef.current.get(sessionId);
      handlers?.delete(handler);
      if (handlers?.size === 0) sessionHandlersRef.current.delete(sessionId);
    };
  }, []);

  const queuePendingMessage = useCallback((message: GlobalWebSocketOutboundMessage): boolean => {
    if (
      message.type === 'game_action' &&
      typeof message.client_move_id === 'string' &&
      pendingMessagesRef.current.some(
        (pendingMessage) =>
          pendingMessage.type === 'game_action' && pendingMessage.client_move_id === message.client_move_id
      )
    ) {
      return false;
    }
    if (pendingMessagesRef.current.length >= MAX_PENDING_MESSAGES) {
      const queueError = new Error('WebSocket pending-message queue is full');
      logger.error(queueError.message, message);
      throw queueError;
    }
    pendingMessagesRef.current.push(message);
    return true;
  }, []);

  const sendMessage = useCallback(
    (message: GlobalWebSocketOutboundMessage): void => {
      const socket = wsRef.current;
      const isReliableChatCommand = message.type === 'chat_message';
      if (isReliableChatCommand) {
        if (typeof message.client_message_id !== 'string' || typeof message.session_id !== 'string') {
          throw new TypeError('chat_message requires client_message_id and session_id');
        }
        if (
          !pendingCommandsRef.current.has(message.client_message_id) &&
          pendingCommandsRef.current.size >= MAX_PENDING_MESSAGES
        ) {
          throw new Error('Pending chat command queue is full');
        }
        const previous = pendingCommandsRef.current.get(message.client_message_id);
        pendingCommandsRef.current.set(message.client_message_id, message);
        try {
          persistPendingCommands();
        } catch (storageError: unknown) {
          if (previous === undefined) {
            pendingCommandsRef.current.delete(message.client_message_id);
          } else {
            pendingCommandsRef.current.set(message.client_message_id, previous);
          }
          throw storageError;
        }
      }

      const sessionReady = !isReliableChatCommand || acknowledgedSessionsRef.current.has(message.session_id as string);
      if (socket?.readyState === WebSocket.OPEN && connectedRef.current && sessionReady) {
        try {
          socket.send(JSON.stringify(message));
          return;
        } catch (sendError: unknown) {
          logger.error('WebSocket send failed; message queued for reconnect', sendError, message);
          try {
            if (!isReliableChatCommand) queuePendingMessage(message);
          } finally {
            socket.close(4002, 'Send failed');
          }
          return;
        }
      }

      if (isReliableChatCommand) {
        logger.warn('Reliable chat command retained until reconnect/ack', {
          clientMessageId: message.client_message_id,
          sessionId: message.session_id,
        });
        return;
      }

      if (queuePendingMessage(message)) {
        logger.warn('WebSocket is disconnected; message queued', {
          pending: pendingMessagesRef.current.length,
          type: message.type,
        });
      }
    },
    [persistPendingCommands, queuePendingMessage]
  );

  const cancelPendingGameAction = useCallback((clientMoveId: string): boolean => {
    const previousLength = pendingMessagesRef.current.length;
    pendingMessagesRef.current = pendingMessagesRef.current.filter(
      (message) => message.type !== 'game_action' || message.client_move_id !== clientMoveId
    );
    return pendingMessagesRef.current.length !== previousLength;
  }, []);

  const joinSession = useCallback(
    (sessionId: string): void => {
      if (joinedSessionsRef.current.has(sessionId)) return;
      joinedSessionsRef.current.add(sessionId);
      if (connectedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'join_session', session_id: sessionId });
      }
    },
    [sendMessage]
  );

  const leaveSession = useCallback(
    (sessionId: string): void => {
      if (!joinedSessionsRef.current.delete(sessionId)) return;
      acknowledgedSessionsRef.current.delete(sessionId);
      if (connectedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'leave_session', session_id: sessionId });
      }
    },
    [sendMessage]
  );

  const isSessionJoined = useCallback((sessionId: string): boolean => {
    return acknowledgedSessionsRef.current.has(sessionId);
  }, []);

  const value = useMemo<GlobalWebSocketContextType>(
    () => ({
      isConnected,
      subscribe,
      subscribeToSession,
      sendMessage,
      cancelPendingGameAction,
      joinSession,
      leaveSession,
      isSessionJoined,
    }),
    [
      isConnected,
      isSessionJoined,
      joinSession,
      leaveSession,
      sendMessage,
      cancelPendingGameAction,
      subscribe,
      subscribeToSession,
    ]
  );

  return <GlobalWebSocketContext.Provider value={value}>{children}</GlobalWebSocketContext.Provider>;
}
